import { Card } from 'canoe';
import { ENDPOINTS, useActions } from 'hammock';
import moment from 'moment';
import React from 'react';

import { ContentType } from '@candu-ai/content/types';
import { useEventing } from 'commons/eventing';
import { transformStringToIdentifier } from 'commons/utils';

import { ContentMetadataForm } from './components/ContentMetadataForm';
import { Stepper } from './components/Stepper';
import { WizardView } from './components/WizardView';
import { CreateContentPayload } from './types';
import { createParagraphstxt, createTitlestxt } from './utils';

interface CreateWithoutTemplateProps {
  onSuccessCreate?: (resource: { id: string }) => void;
  folderId?: string;
  context: {
    newModals?: boolean;
    contentType: ContentType;
    hasExtension?: boolean;
    skipForm: boolean;
    withSnapshots?: boolean;
  };
}

export const CreateWithoutTemplate = ({
  onSuccessCreate,
  context,
  folderId,
}: CreateWithoutTemplateProps) => {
  const { create } = useActions(ENDPOINTS.contentMetadata);
  const eventing = useEventing();

  const createContent = ({ name, folderId: selectedFolder, contentType }: CreateContentPayload) =>
    create({
      body: {
        name,
        folderId: selectedFolder,
        slug: transformStringToIdentifier(name),
        contentType,
      },
      invalidates: {
        [ENDPOINTS.contentDashboard]: true,
      },
      onSuccessNotification: context?.skipForm ? null : undefined,
      onErrorNotification: context?.skipForm ? null : undefined,
    });

  const onSubmit = (payload: CreateContentPayload) => {
    createContent(payload).then((resource: { id: string }) => {
      eventing.track('dashboard.content.created');
      onSuccessCreate?.(resource);
    });
  };

  if (context?.skipForm) {
    const payload = {
      name: `${context?.contentType} ${moment(new Date().getTime()).format(
        'YYYY-MM-DD[T]HH:mm:ss',
      )}`,
      folderId: Number.isNaN(Number(folderId)) ? null : Number(folderId),
      contentType: context?.contentType,
    };

    onSubmit(payload);
    return null;
  }

  return (
    <Card size="xl">
      <Stepper currentStep={2} withSnapshots={!!context?.withSnapshots} />
      <WizardView
        title={createTitlestxt[context?.contentType || 'Inline']}
        subTitle={createParagraphstxt[context?.contentType || 'Inline']}
      >
        <ContentMetadataForm
          onSubmit={onSubmit}
          withSnapshots={context?.withSnapshots}
          folderId={folderId}
          contentType={context?.contentType || 'Inline'}
        />
      </WizardView>
    </Card>
  );
};
